import * as React from "react";
import Loading from "../ui/loading/Loading";
import {none, option, Option} from "ts-option";
import {connect, Dispatch} from "react-redux";
import {RootState} from "../../store/reducer/root/RootState";
import IconWrapper from "../../hiberUiKit/src/atoms/Icon/IconWrapper";
import {Icon} from "../../hiberUiKit/src/atoms/Icon/Icon";
import IconLarge from "../../hiberUiKit/src/atoms/Icon/IconLarge";
import DataGrid from "../../hiberUiKit/src/organisms/DataGrid/DataGrid";
import QuoteItem from "../../model/quote/QuoteItem";
import Tooltip from "../../hiberUiKit/src/atoms/Tooltip/Tooltip";
import QuoteDetails, {QuoteStatusEnum} from "../../model/quote/QuoteDetails";
import Toast from "../helper/Toast";
import IndividualContact from "../../model/contact/IndividualContact";
import DateUtils from "../../utils/DateUtils";
import {CommonDetailsStatusRow} from "../accounting/common/CommonDetailsStatusRow";
import {default as CommonDetailsInfoRow, DetailsInfoRowItem} from "../accounting/common/CommonDetailsInfoRow";
import {CommonDetailsSummary, DetailsSummaryItem} from "../accounting/common/CommonDetailsSummary";
import {notifyBugsnagAndShowToast} from "../../utils/Utils";
import Amount from "../../model/common/Amount";
import DropDown from "../../hiberUiKit/src/molecules/DropDown/DropDown";
import FinanceProduct from "../../model/financeProduct/FinanceProduct";
import {updateQuoteFinanceProduct} from "../../store/action/quote/quote";
import "../accounting/common/commonDetailsSummary.scss";
import "./quoteDetailsView.scss";


interface ComponentProps {
	refNumPathItem1: string
	isLoading: boolean
	/**
	 * Quote details data.
	 */
	quoteDetails: Option<QuoteDetails>;
	/**
	 * Lead contact data.
	 */
	contact: Option<IndividualContact>;

	onBack: () => void

	readonly?: boolean
}


interface ReduxProps {
	/**
	 * Finance products.
	 */
	financeProducts: Option<FinanceProduct[]>;
}

interface DispatchProps {
	/**
	 * Update quote finance product action.
	 * @param quoteId
	 * @param financeProductId
	 */
	updateQuoteFinanceProduct: (quoteId: string, financeProductId: string) => void;
}

interface State {
	tooltipQuoteItemIndex: Option<number>;
	summaryInfoTooltipVisible: boolean;
}

type Props = ComponentProps & ReduxProps & DispatchProps;

/**
 * Quote details view.
 */
class QuoteDetailsView extends React.PureComponent<Props, State> {

	quoteItemInfoRef: HTMLDivElement[] = [];
	summaryInfoParentRef: HTMLDivElement;

	constructor(props: Props) {
		super(props);
		this.state = {
			tooltipQuoteItemIndex: none,
			summaryInfoTooltipVisible: false
		};
	}

	render() {
		const {quoteDetails} = this.props;
		const vatRegistered = quoteDetails.map(qd => qd.vatRegistered).getOrElse(false);
		const quoteAccepted = quoteDetails.map(qd => qd.quoteStatus === QuoteStatusEnum.Accepted).getOrElse(false);
		const quoteSent = quoteDetails.map(qd => qd.sentToCustomer).getOrElse(false);
		const refNumPathItem2 = quoteDetails.flatMap(
			q => q.referenceNumber.map(
				refNum => `Quote ID# ${refNum}${q.versionNumber > 1 ? ` v${q.versionNumber}` : ""}`)).orNull;
		return (
			<div className={"l-page"}>
				<Loading isLoading={this.props.isLoading}>
					<CommonDetailsStatusRow icon={Icon.Quote}
											title={"Quote"}
											onBackClick={this.props.onBack}
											status={quoteAccepted ? "ACCEPTED" : "READY TO SEND"}
											additionalComponent={this.renderQuoteSentInfo(quoteSent)}
											refNumPath={[this.props.refNumPathItem1, refNumPathItem2]}
											secondAdditionalComponent={this.renderFinanceSelect()}/>
					{this.renderDetailsInfoRow()}
					{this.renderTable(vatRegistered)}
					<div className="c-common-details_common-summary_external-container">
						{this.renderSummary(vatRegistered)}
					</div>
				</Loading>
				{this.state.tooltipQuoteItemIndex.map(index => this.renderQuoteItemInfoTooltip(index, vatRegistered)).orNull}
				{this.state.summaryInfoTooltipVisible && this.renderSummaryInfoTooltip()}
			</div>
		);
	}

	renderFinanceSelect = () => {
		const products = this.props.financeProducts.getOrElse([]);
		if (!this.props.readonly && products.length && this.props.quoteDetails.map(qd => qd.payByFinance).getOrElse(false)) {
			const financeProductId = this.props.quoteDetails.flatMap(qd => qd.financeProductId).orNull;
			return (
				<DropDown placeholder={"Finance products"}
						  items={products}
						  itemToId={(product: FinanceProduct) => product.id}
						  itemToLabel={(product: FinanceProduct) => product.label}
						  onSelect={(product: FinanceProduct) => this.onFinanceProductSelect(option(product))}
						  itemDisabled={(product: FinanceProduct) => product.disabled}
						  clearable
						  onClearClick={() => this.onFinanceProductSelect(none)}
						  selectedItemId={financeProductId}/>
			)
		}
	};

	onFinanceProductSelect = (product: Option<FinanceProduct>) => {
		this.props.quoteDetails.map(qd => this.props.updateQuoteFinanceProduct(qd.id, product.map(p => p.id).orNull));
	};

	renderDetailsInfoRow = () => {
		const infoRowItem1: DetailsInfoRowItem = {label: "To", value: this.props.contact.map(c => c.data.displayName()).orNull};
		const infoRowItem2: DetailsInfoRowItem = {
			label: "Quote date",
			value: this.props.quoteDetails.map(q => q.quoteDate.format(DateUtils.DAILY_PLAN_DATE_FORMAT)).orNull
		};
		const infoRowItem3: DetailsInfoRowItem = {
			label: "Quote #",
			value: this.props.quoteDetails.flatMap(q => q.referenceNumber).orNull
		};
		return <CommonDetailsInfoRow items={[infoRowItem1, infoRowItem2, infoRowItem3]}/>
	};

	renderQuoteSentInfo = (quoteSent: boolean) => {
		if (quoteSent) {
			return (
				<div className="c-quote-details_sent-info">
					<span>Quote sent to customer</span>
					<img src="/assets/img/icons/icn-success-cmn.svg"/>
				</div>
			);
		}
		return (
			<div className="c-quote-details_not-sent-info">
				Quote has not been sent to the customer
			</div>
		);
	};

	renderQuoteItemInfoTooltip = (index: number, vatRegistered: boolean) => {
		const quoteItem = this.props.quoteDetails.map(qd => qd.items).getOrElse([])[index];
		if (!quoteItem) return null;
		const unitPrice = this.props.quoteDetails.map(
			qd => vatRegistered ? quoteItem.unitPrice : quoteItem.calculateUnitPriceWithVat(qd.vat));
		return (
			<Tooltip customClass="c-quote-details_item-tooltip"
					 parentRef={this.quoteItemInfoRef[index]}
					 dropdownAlwaysOnRight={true}
					 onOutsideClick={() => this.setState({tooltipQuoteItemIndex: none})}>
				<div className="c-quote-details_item-tooltip-row">
					<div className="c-quote-details_tooltip-label">Cost price</div>
					<div className="c-quote-details_tooltip-value">{unitPrice.map(price => price.format()).orNull}</div>
				</div>
				<div className="c-quote-details_item-tooltip-row">
					<div className="c-quote-details_tooltip-label">Markup %</div>
					<div className="c-quote-details_tooltip-value">{quoteItem.markup}</div>
				</div>
			</Tooltip>
		)
	};

	renderSummaryInfoTooltip = () => {
		return (
			<Tooltip customClass="c-quote-details_summary-tooltip"
					 parentRef={this.summaryInfoParentRef}
					 arrowOnTop={true}
					 onOutsideClick={() => this.setState({summaryInfoTooltipVisible: false})}>
				<div className="c-quote-details_summary-tooltip-row">
					<div className="c-quote-details_tooltip-label">Installer earnings</div>
					<div className="c-quote-details_tooltip-value">
						{this.props.quoteDetails.map(q => q.getInstallerGross().format()).orNull}
					</div>
				</div>
				<div className="c-quote-details_summary-tooltip-row">
					<div className="c-quote-details_tooltip-label">hiber earnings</div>
					<div className="c-quote-details_tooltip-value">
						{this.props.quoteDetails.map(q => q.getHiberCommision().format()).orNull}
					</div>
				</div>
			</Tooltip>
		)
	};

	renderUnitPriceCellContent = (item: QuoteItem, index: number, vatRegistered: boolean) => {
		const unitPrice = this.props.quoteDetails.map(
			qd => vatRegistered ? item.unitPrice : item.calculateUnitPriceWithVat(qd.vat));
		return (
			<div className="c-quote-details_unit-price-cell-container">
				{unitPrice.map(price => price.format()).orNull}
				<div ref={(ref) => this.quoteItemInfoRef[index] = ref}
					 className="c-quote-details_unit-price-icon-container"
					 onMouseOver={() => this.setState({tooltipQuoteItemIndex: option(index)})}
					 onMouseLeave={() => this.setState({tooltipQuoteItemIndex: none})}>
					<IconLarge icon={Icon.Info} variant={"blue_medium"} transform={"rotate(180)"}/>
				</div>
			</div>
		)
	};

	renderSummary = (vatRegistered: boolean) => {
		const quoteDetails = this.props.quoteDetails;
		const vatInclusion = vatRegistered ? 'ex' : 'in';
		const summaryItem1: DetailsSummaryItem = {
			label: "Parts margin share",
			value: quoteDetails.map(q => q.markupAmount.format()).orNull
		};
		const summaryItem2: DetailsSummaryItem = {
			label: "Installer parts margin share",
			value: quoteDetails.flatMap(q => q.installerBonusAmount.map(hc => hc.format())).orNull,
			italic: true
		};
		const summaryItem3: DetailsSummaryItem = {
			label: `Parts price (${vatInclusion}. VAT)`,
			value: quoteDetails.map(q => q.getPartsPrice().format()).orNull
		};
		const summaryItem4: DetailsSummaryItem = {
			label: `Sub-total (${vatInclusion}. VAT)`,
			value: quoteDetails.map(q => q.totalPriceBeforeVat.format()).orNull
		};
		const summaryItem5: DetailsSummaryItem = {
			label: "VAT",
			value: quoteDetails.flatMap(q => q.vatAmount).map(va => va.format()).orNull
		};
		const summaryTotalItem: DetailsSummaryItem = {
			label: "Total",
			value: quoteDetails.map(q => q.totalPrice.format()).orNull
		};
		const lastItem: DetailsSummaryItem = {
			label: "Reward points",
			value: this.props.quoteDetails.flatMap(qd => qd.rewardPoints).map(rp => rp.toString()).orNull
		};

		let summaryItems = [summaryItem1, summaryItem2, summaryItem3, summaryItem4];
		if (vatRegistered) {
			summaryItems = summaryItems.concat(summaryItem5);
		}
		return <CommonDetailsSummary items={summaryItems}
									 totalItem={summaryTotalItem}
									 renderTooltipParentElement={this.renderTooltipParent}
									 lastItem={lastItem}
									 style={{marginRight: 240}}/>
	};

	renderTooltipParent = () => {
		return (
			<div onMouseOver={() => this.setState({summaryInfoTooltipVisible: true})}
				 ref={(ref) => this.summaryInfoParentRef = ref}
				 onMouseLeave={() => this.setState({summaryInfoTooltipVisible: false})}
				 className="c-common-summary_icon-wrapper">
				<IconWrapper icon={Icon.Info} variant={"blue_medium"} size={42} transform={"rotate(180)"}/>
			</div>
		);
	};

	renderTable = (vatRegistered: boolean) => {
		return (
			<DataGrid>
				<thead>
				<tr className="c-common-details_table-header-row">
					<th className="c-common-details_table-desc-cell">DESCRIPTION</th>
					<th>{`UNIT PRICE (${vatRegistered ? `EXCL` : `INCL`}. VAT)`}</th>
					<th>QTY</th>
					<th>UNIT TOTAL</th>
				</tr>
				</thead>
				<tbody>
				{this.props.quoteDetails.map(q => q.items).getOrElse([]).map((item, index) =>
					<tr key={index} className="c-common-details_table-row">
						<td className="c-common-details_table-desc-cell">{item.name}</td>
						<td className="c-quote-details_table-unit-price-cell">
							{this.renderUnitPriceCellContent(item, index, vatRegistered)}
						</td>
						<td>{item.quantity}</td>
						<td>{vatRegistered ? item.totalPrice.format() : item.totalPriceWithVat.format()}</td>
					</tr>)}
				{this.props.quoteDetails.map(q =>
					(q.labourTotalCost.cents > 0) &&
					<tr key={"Labour"} className="c-common-details_table-row">
						<td className="c-common-details_table-desc-cell">Installation labour</td>
						<td/>
						<td/>
						<td>{q.labourTotalCost.format()}</td>
					</tr>).orNull}
				</tbody>
			</DataGrid>);
	};
}

function mapStateToProps(state: RootState) {
	const financeProducts = state.financeProduct.financeProducts;
	return ({financeProducts});
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		updateQuoteFinanceProduct: (quoteId: string, financeProductId: string) => dispatch(
			updateQuoteFinanceProduct(
				quoteId,
				financeProductId,
				() => Toast.success("Finance product has been set.", "Set finance product"),
				(error) => notifyBugsnagAndShowToast(error, "Set finance product"))
		)
	}
}

export default connect<ReduxProps, DispatchProps, ComponentProps>(mapStateToProps, mapDispatchToProps)(QuoteDetailsView)
